import * as DG from "ui/dialogs"
import { Key, useCallback, useState } from "react"

import { SupplierProductTuple, SupplierTuple } from "models/supplier"
import { queryCache, useQuery } from "react-query"

import { BREAKDOWN_SEMANTIC_CODE } from "routes/Base/Admin/MarketVendors/Details/RateCard/RateCardDetails/BaseRate/utils"
import Button from "ui/buttons/Button"
import { ChangedPlanRecord } from "../types"
import { Loading } from "components/Loading"
import { MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { NEGOTIATION_TYPES } from "models/ratecard"
import { Paginated } from "api/types"
import { RateCardTable } from "./RateCardTable"
import { css } from "@emotion/css"
import { fetchBaseRates } from "api/plans"
import get from "lodash/get"
import { getDualFields } from "../utils"
import { set } from "lodash/fp"
import { useAutoCompute } from "../components/AutoCompute/useAutoCompute"
import { useCellDialog } from "../useCellDialog"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useTargetFieldIds } from "../Overview/Targets/useTargetFieldIds"
import { useUndoStack } from "../useUndoStack"

const BASE_RATE_SEMANTIC = "rateCardCost"

type Props = {
  isBooking?: boolean
}

export function RateCardDialog(props: Props) {
  const { t } = useI18n()
  const { id: planId } = useParams<{ id: string }>()
  const [pickedValues, setPickedValues] = useState<{
    baseRateId: string
    breakdown: Key
    unitCost: number
  }>()
  const { isBooking } = props
  const { value: cellDialog, onClose } = useCellDialog()
  const { rowId: id } = cellDialog?.fieldInfo || {}
  const autoCompute = useAutoCompute()
  const tableSource = useTableSource()
  const readFrom = useSnapshot(tableSource.state)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const values = !!id ? readFrom[id] : undefined
  const { ancestorMediaId, action } = values || {}
  const updatable = !action || action === "UPDATE_ORDER"
  const writeTo = tableSource.state
  const { primary: fields } = getDualFields(isBooking)
  const open = cellDialog?.type === "rate_card"

  const ps = new URLSearchParams([
    ["configScope", "MEDIA_PLAN"],
    ["configOwnerId", planId],
  ])
  const mediaTypes = queryCache.getQueryData<Paginated<MediaType>>([
    "media-types",
    ps.toString(),
  ])

  const baseRateParams = getBaseRateParams(
    mediaTypes?.data || [],
    values as ChangedPlanRecord,
    isBooking,
  )

  const activeFlowPlanItems = Object?.keys(readFrom)?.map((s) => readFrom[s])
  const mediaId = values?.ancestorMediaId
  const getTargetIds = useTargetFieldIds()
  const { targetB: targetBId } = getTargetIds(mediaId)
  const targetBIds =
    (Object.values(activeFlowPlanItems)?.find(
      (fpi) => fpi.type === "media_type_plan" && fpi.mediaId === mediaId,
    )?.["planningMediaFields"]?.[targetBId] as string[]) ?? []

  !!targetBIds?.length && baseRateParams.set("targetIds", targetBIds?.join(","))

  const { data: rateCard, isLoading } = useQuery(
    ["media-plan-base-rate", planId, baseRateParams.toString()],
    fetchBaseRates,
    {
      enabled: open && !!baseRateParams.get("supplierId"),
    },
  )

  const mediaFields = mediaTypes?.data.find(
    (mt) => mt.id === ancestorMediaId,
  )?.mediaFields

  const rateCardCostFieldId = mediaFields?.find(
    (mf) => mf.semanticCode === BASE_RATE_SEMANTIC,
  )?.id

  const dimensionSemanticFieldIdMap = rateCard?.baseRate?.dimensions?.reduce(
    (all, dimension) => {
      const semanticCode = dimension.mediaFieldSemanticCode
      const mediaFieldId = mediaFields?.find(
        (mf) => mf.semanticCode === semanticCode,
      )?.id
      if (mediaFieldId) all[semanticCode] = mediaFieldId
      return all
    },
    {} as Record<string, string>,
  )

  const handleBaseRatePick = useCallback(
    (baseRateId: string, breakdown: Key, unitCost: number) => {
      setPickedValues({ baseRateId, breakdown, unitCost })
    },
    [],
  )

  if (!id) return null

  const handleResetAndClose = () => {
    setPickedValues(undefined)
    autoCompute.compute(writeTo[id] as ChangedPlanRecord)
    onClose()
  }

  const handleSubmit = () => {
    if (pickedValues) {
      const { baseRateId, breakdown, unitCost } = pickedValues
      const record = rateCard?.baseRate?.data?.find((d) => d.id === baseRateId)
      if (record) {
        const updatableMediaFields = Object.keys(record).reduce(
          (all, rk) => {
            const mediaFieldId = dimensionSemanticFieldIdMap?.[rk]
            const value =
              rk !== BREAKDOWN_SEMANTIC_CODE ? record[rk] : breakdown
            if (mediaFieldId && value) all[mediaFieldId] = value
            return all
          },
          {} as Record<string, any>,
        )

        if (rateCardCostFieldId)
          updatableMediaFields[rateCardCostFieldId] = {
            defaultCost: unitCost,
          }

        globalUndoState.push([writeTo[id] as ChangedPlanRecord])
        const fieldValues = get(values, fields)
        writeTo[id] = set(
          fields,
          { ...fieldValues, ...updatableMediaFields },
          writeTo[id],
        )
        updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
        handleResetAndClose()
      }
    }
  }

  const dialogId = "rate-card-dialog"

  return (
    <DG.Dialog
      maxWidth="sm"
      id={dialogId}
      open={open}
      onClose={handleResetAndClose}
      className={css`
        .MuiDialog-paperWidthSm {
          max-width: 800px;
        }
      `}
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={dialogId} className="BodyBody-1BlackBold">
          {t("rate.card.selection.title")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={handleResetAndClose} />
      </DG.DialogHeader>
      <DG.DialogContent paddingTop>
        {isLoading ? (
          <Loading />
        ) : (
          <div
            className={css`
              overflow: auto;
              max-height: 440px;
            `}
          >
            <RateCardTable
              dimensions={rateCard?.baseRate?.dimensions}
              data={rateCard?.baseRate?.data}
              handleBaseRatePick={handleBaseRatePick}
              pickedValues={pickedValues}
            />
          </div>
        )}
      </DG.DialogContent>
      <DG.DialogFooter>
        <DG.DialogActions>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleResetAndClose}
          >
            {t("actions.cancel")}
          </Button>
          <Button
            disabled={!pickedValues}
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          >
            {t("actions.done")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

export function getBaseRateParams(
  mediaTypes: MediaType[],
  rowValues: ChangedPlanRecord,
  isBooking?: boolean,
) {
  const { primary: fields } = getDualFields(isBooking)
  const mediaId = rowValues?.ancestorMediaId

  const mediaType = mediaTypes?.find((mt) => mt.id === mediaId)
  const defaultFieldIds = mediaType?.defaultFieldIdBySemantic
  const {
    vendor: vendorFieldId,
    vendorProduct: vendorProductFieldId,
    start: startDateFieldId,
    location: locationFieldId,
    negotiationType: negotiationTypeId,
  } = defaultFieldIds || {}

  const supplier = get(rowValues, `${fields}.${vendorFieldId}`) as SupplierTuple
  const supplierId = supplier?.mediaOwnerId
  const startDate = get(rowValues, `${fields}.${startDateFieldId}`)
  const location = get(rowValues, `${fields}.${locationFieldId}`)
  const supplierProduct = get(
    rowValues,
    `${fields}.${vendorProductFieldId}`,
  ) as SupplierProductTuple
  const supplierProductId = supplierProduct?.supplierProductId
  const negotiationTypeObj = get(rowValues, `${fields}.${negotiationTypeId}`)
  const negotiationType = negotiationTypeObj?.[
    "negotiationType"
  ] as (typeof NEGOTIATION_TYPES)[number]

  const _params = {
    mediaTypeSemanticCode: mediaType?.semanticCode,
    supplierId,
    startDate,
    location,
    supplierProductId,
    negotiationType,
  }

  const params = new URLSearchParams()
  Object.entries(_params).forEach(([key, value]) => {
    !!value && params.set(key, value)
  })

  return params
}
